import React, { useState } from 'react'
import api from 'helpers/api'
import Button from 'components/button'
import ActionDialog from 'components/dialog/action'
import { Lock } from 'components/icons'
import { useApplicationStore } from 'hooks/application'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { ICustomViewVersion } from 'types'
import { usePublishVersion } from './versions-panel'

/**
 * Over the page while its newest version isn't published: what the owner sees here is theirs alone until they
 * Publish it, or Discard it to go back to the published page. A quiet bar in the open tab's grey, so the tab runs
 * down into it like a bookmark; the same height as the MortaAI panel's header (53px), so the two read as one row.
 */
export default function PublishBanner({ version }: { version: ICustomViewVersion }) {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const publishVersion = usePublishVersion()
  const [busy, setBusy] = useState<'publish' | 'discard'>()
  const [confirming, setConfirming] = useState(false)
  const viewId = spreadsheetData.viewDetails.publicId
  const hasPublished = Boolean(spreadsheetData.viewDetails.customView)

  const publish = () => {
    if (busy) return
    setBusy('publish')
    publishVersion(version)
      .catch(displayErrorMessage)
      .finally(() => setBusy(undefined))
  }

  // A failure reaches the confirmation, which says so and stays open to try again.
  const discard = () => {
    setBusy('discard')
    return (
      api
        .discardCustomViewVersions(viewId)
        // The view, as an owner gets it, has no unpublished version now.
        .then(() => api.getTableView(viewId))
        .then((response) => {
          setSpreadsheetData({ type: 'SET_CUSTOM_VIEW', viewId, customView: response.data.customView ?? null })
          setSpreadsheetData({ type: 'SET_CUSTOM_VIEW_LATEST', viewId, latest: response.data.customViewLatest ?? null })
          setSnackbarMessage({ status: 'success', message: 'Changes discarded' })
        })
        .finally(() => setBusy(undefined))
    )
  }

  return (
    <>
      <div
        className="@container flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 min-h-[53px] px-4 py-2 border-b border-divider bg-surface-sunken shrink-0"
        data-testid="custom-view-publish-banner"
      >
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
          <span className="text-sm font-semibold text-body whitespace-nowrap">Preview</span>
          <span className="flex items-center gap-1 text-xs text-muted whitespace-nowrap">
            <Lock className="w-3 h-3" />
            Changes visible to you only
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            internalType="outline"
            onClick={() => setConfirming(true)}
            disabled={busy !== undefined}
            aria-label="Discard changes"
          >
            {/* Short labels when the banner is narrow, e.g. beside the MortaAI panel. */}
            <span>
              Discard<span className="@max-[520px]:hidden"> changes</span>
            </span>
          </Button>
          <Button
            internalType="outline"
            // Morta's blue (deep enough to carry white text), rather than the hub's colour.
            className="bg-blue-strong text-on-solid border-transparent hover:bg-blue-strong hover:brightness-95"
            onClick={publish}
            disabled={busy !== undefined}
            aria-label="Publish changes"
          >
            {busy === 'publish' ? (
              'Publishing…'
            ) : (
              <span>
                Publish<span className="@max-[520px]:hidden"> changes</span>
              </span>
            )}
          </Button>
        </div>
      </div>
      {confirming && (
        <ActionDialog
          title="Discard changes?"
          description={
            hasPublished
              ? 'The changes made since this page was last published are deleted, and the view goes back to the published page. This can’t be undone.'
              : 'This page has never been published, so every version of it is deleted and the view goes back to its prompt. This can’t be undone.'
          }
          confirmLabel="Discard changes"
          danger
          onClose={() => setConfirming(false)}
          // The dialog stays open to try again; the error says why.
          onConfirm={() =>
            discard().catch((error) => {
              displayErrorMessage(error)
              throw error
            })
          }
        />
      )}
    </>
  )
}
